import React, { useEffect, useState } from "react";
import { listarUsuarios, excluirUsuario } from "../services/api";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2, Search, Plus } from "lucide-react";

const ListaUsuarios = ({ token }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosOriginais, setUsuariosOriginais] = useState([]);
  const [busca, setBusca] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await listarUsuarios(token);
        setUsuarios(response.data);
        setUsuariosOriginais(response.data);
      } catch (error) {
        console.error("Erro ao listar usuários:", error);
      }
    };
    fetchData();
  }, [token]);

  const handleEditar = (id) => {
    navigate(`/usuarios/editar/${id}`);
  };

  const filtrarUsuario = (usuario, termoBusca = busca) => {
    const termo = (termoBusca || "").toLowerCase();
    return (
      usuario.email?.toLowerCase().includes(termo) ||
      usuario.nome?.toLowerCase().includes(termo)
    );
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este usuário?")) return;

    try {
      await excluirUsuario(id, token);
      const atualizados = usuariosOriginais.filter((u) => u.id !== id);
      setUsuariosOriginais(atualizados);
      setUsuarios(atualizados.filter((u) => filtrarUsuario(u)));
      alert("Usuário excluído com sucesso!");
    } catch (error) {
      console.error("Erro ao excluir usuário:", error);
      alert("Erro ao excluir usuário!");
    }
  };

  const handleBuscaChange = (e) => {
    const termo = e.target.value;
    setBusca(termo);
    setUsuarios(usuariosOriginais.filter((u) => filtrarUsuario(u, termo)));
  };

  return (
    <div
      className="
        zen-card
        rounded-2xl p-6 shadow-sm
        dark:backdrop-blur
      "
    >
      {/* header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Listagem de Usuários
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-300/80">
            Pesquise por e-mail ou nome e gerencie usuários.
          </p>
        </div>

        <button
          className="
            inline-flex items-center justify-center px-4 py-2 rounded-xl font-semibold text-sm transition
            bg-blue-600 text-white hover:bg-blue-500
          "
          onClick={() => navigate("/usuarios/novo")}
        >
          <Plus size={18} className="mr-2" />
          Novo Usuário
        </button>
      </div>

      {/* filtros */}
      <div
        className="
          rounded-2xl p-4 mb-6 zen-card border-gray-200
          dark:bg-white/5 dark:border-white/10
        "
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Buscar
            </label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Buscar usuário..."
                value={busca}
                onChange={handleBuscaChange}
                className="
                  pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                  zen-card text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
              />
            </div>
          </div>
        </div>
      </div>

      {/* tabela */}
      <div className="overflow-x-auto">
        <div
          className="
            min-w-full rounded-2xl
            zen-card
            dark:border-white/10 dark:bg-white/5
          "
        >
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Email
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Nome
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Ações
                </th>
              </tr>
            </thead>

            <tbody className="text-gray-700 dark:text-slate-200">
              {usuarios.map((usuario) => (
                <tr
                  key={usuario.id}
                  className="
                    border-t border-gray-200 hover:bg-gray-50 transition
                    dark:border-white/10 dark:hover:bg-white/5
                  "
                >
                  <td className="py-3 px-4">{usuario.email}</td>
                  <td className="py-3 px-4">{usuario.nome}</td>

                  <td className="text-center py-3 px-4">
                    <div className="inline-flex items-center gap-1">
                      <button
                        className="
                          p-2 rounded-full transition
                          text-blue-600 hover:bg-blue-100
                          dark:text-blue-300 dark:hover:bg-blue-500/10
                        "
                        onClick={() => handleEditar(usuario.id)}
                        title="Editar Usuário"
                      >
                        <Edit size={18} />
                      </button>

                      <button
                        className="
                          p-2 rounded-full transition
                          text-red-600 hover:bg-red-100
                          dark:text-red-300 dark:hover:bg-red-500/10
                        "
                        onClick={() => handleExcluir(usuario.id)}
                        title="Excluir Usuário"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {usuarios.length === 0 && (
                <tr>
                  <td
                    colSpan="3"
                    className="py-10 text-center text-gray-500 dark:text-slate-300/80"
                  >
                    Nenhum usuário encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ListaUsuarios;
